/**
 * As ferramentas do diagrama que o menu do Sistema de hoje tem.
 *
 * Todas existiam lá e não existiam aqui. Cada uma diz, no seu lugar, o que faz de
 * diferente do original — e as duas destrutivas dizem que são.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import { corpoDosPares, type ComponenteCandidato, type LinhaMarcada } from "./ligacoesEmLote"

/** Derruba tudo que depende do grafo do diagrama. */
function usarDerrubar(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ["diagrama-montagem", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-verificacao", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
  }
}

// ---------------------------------------------------------------- margens

export type MargensCalculadas = {
  diagramaId: number
  componentes: number
  ajustados: number
}

/**
 * Recalcula a margem de baixo de cada componente — o "Calcular a margem de
 * acordo com o componente" do menu antigo.
 *
 * **Só aumenta**: o motor grava apenas quando o texto precisa de mais espaço do
 * que os terminais já dão. Rodar duas vezes seguidas ajusta zero, e isso é
 * sinal de que está certo, não de que falhou.
 */
export function usarCalcularMargens(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<MargensCalculadas>(
        `api/diagramas/${diagramaId}/margens`, undefined, noProjeto(projetoId))
      return r.data
    },
    onSuccess: derrubar,
  })
}

// ------------------------------------------------------- conferir modelos

export type TerminalForaDoModelo = {
  diagramaLigacaoId: number
  diagramaComponenteId: number
  pinoutId: number
  modeloId: number
  componenteDescricao: string
  conectorDescricao: string
  circuitoDescricao: string
  pino: string
  /** Serve a **outro** modelo do mesmo diagrama — está certo onde está. */
  serveOutroModelo: boolean
}

export type ConferenciaDeModelos = {
  diagramaId: number
  modelos: number[]
  orfaos: number
  deOutroModelo: number
  terminais: TerminalForaDoModelo[]
}

export function usarConferirModelos(diagramaId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["diagrama-conferir-modelos", diagramaId] as const,
    enabled: ligado && diagramaId > 0,
    staleTime: 10_000,
    queryFn: () => obter<ConferenciaDeModelos>(`api/diagramas/${diagramaId}/conferir-modelos`, {}),
  })
}

/**
 * **DESTRUTIVO.** Apaga os terminais que a conferência apontou.
 *
 * Por padrão só os **órfãos** — os que não servem a modelo nenhum do diagrama.
 * `comoOLegado` reproduz o "Excluir" antigo, que apaga também os que servem a
 * outro modelo: 90% do que ele removeria nos diagramas publicados de hoje.
 */
export function usarAplicarModelos(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (comoOLegado: boolean) => {
      const r = await api.post<{ ligacoesRemovidas: number; componentesRemovidos: number }>(
        `api/diagramas/${diagramaId}/conferir-modelos/aplicar?comoOLegado=${comoOLegado}`,
        undefined, noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => {
      derrubar()
      qc.invalidateQueries({ queryKey: ["diagrama-conferir-modelos", diagramaId] })
    },
  })
}

// --------------------------------------------------- componente sem ligação

export function usarIncluirComponenteAvulso(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (componenteId: number) => {
      const r = await api.post<{ diagramaComponenteId: number }>(
        `api/diagramas/${diagramaId}/componentes`, { componenteId }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: derrubar,
  })
}

// ----------------------------------------------------------- cadeia de SVG

export type RedesenhoDaCadeia = {
  diagramaId: number
  diagramaDoTopo: number
  elos: number
  bytes: number
  milissegundos: number
}

/**
 * Redesenha a cadeia de diagramas ligados — o "Salvar SVG incluindo vínculo
 * acima". Sobe o vínculo até o topo, desenha de lá e grava o mesmo traço nos
 * dois. **Grava a coluna que o site do cliente serve.**
 */
export function usarRedesenharCadeia(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<RedesenhoDaCadeia>(
        `api/diagramas/${diagramaId}/desenhar/cadeia`, undefined,
        { ...noProjeto(projetoId), timeout: 180_000 })
      return r.data
    },
    onSuccess: derrubar,
  })
}

// ------------------------------------------------------ posicionamentos

export type PosicionamentoPadrao = {
  id: number
  sistemaId: number
  apelido: string
  cor: string
  posicao: string
  ordem: number
}

export function usarPosicionamentosDoSistema(sistemaId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["posicionamentos-padrao", sistemaId] as const,
    enabled: ligado && sistemaId > 0,
    staleTime: 30_000,
    queryFn: () => obter<PosicionamentoPadrao[]>("api/posicionamentos-padrao", { sistemaId }),
  })
}

/**
 * Aplica padrões a componentes: cada par leva **apelido, ordem + lado e cor**.
 *
 * **O apelido é do componente no projeto**, não do componente neste diagrama —
 * trocá-lo aqui troca em todo diagrama que o usa. É o que o sistema antigo faz,
 * e a resposta diz quantos apelidos mudaram.
 */
export function usarAplicarPosicionamentos(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (pares: { posicionamentoPadraoId: number; diagramaComponenteId: number }[]) => {
      const r = await api.post<{ aplicados: number; apelidos: number }>(
        `api/diagramas/${diagramaId}/posicionamentos/aplicar`, { pares }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: derrubar,
  })
}

// ------------------------------------------------- do componente: terminais

/**
 * **DESTRUTIVO.** Funde terminais do componente: o primeiro fica, os outros são
 * **apagados** — não é a lixeira, não voltam. É o "Agrupar ligações" do menu,
 * para o componente que ficou com o mesmo pino repetido.
 */
export function usarAgruparLigacoes(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ componenteId, ids }: { componenteId: number; ids: number[] }) => {
      const r = await api.post<{ removidas: number; terminalQueFicou: number }>(
        `api/diagramas/componentes/${componenteId}/ligacoes/agrupar`,
        { diagramaLigacaoIds: ids }, noProjeto(projetoId))
      return { ...r.data, componenteId }
    },
    onSuccess: (r) => {
      derrubar()
      qc.invalidateQueries({ queryKey: ["componente-terminais", r.componenteId] })
      qc.invalidateQueries({ queryKey: ["componente-desenho", r.componenteId] })
    },
  })
}

/** É o mesmo record da inclusão do diagrama. */
export type CandidatoDoComponente = ComponenteCandidato

/**
 * Os pinos que **este componente** pode ganhar — o "Incluir ligações do
 * componente" do menu. É lista diferente da do diagrama: o motor parte do pinout
 * deste componente, e não do principal.
 */
export function usarCandidatosDoComponente(diagramaComponenteId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["componente-candidatos", diagramaComponenteId] as const,
    enabled: ligado && diagramaComponenteId > 0,
    staleTime: 10_000,
    queryFn: () => obter<CandidatoDoComponente[]>(
      `api/diagramas/componentes/${diagramaComponenteId}/ligacoes/candidatos`, {}),
  })
}

/**
 * Grava as linhas marcadas como ligações **deste componente** — os dois lados,
 * cada um apontando para o outro; nada liga no principal.
 */
export function usarIncluirLigacoesDoComponente(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ componenteId, pares }: { componenteId: number; pares: LinhaMarcada[] }) => {
      const r = await api.post<{ componentesInseridos: number; ligacoesInseridas: number }>(
        `api/diagramas/componentes/${componenteId}/ligacoes`,
        { ...corpoDosPares(pares), completo: false }, noProjeto(projetoId))
      return { ...r.data, componenteId }
    },
    onSuccess: (r) => {
      derrubar()
      // O que entrou sai da lista: a API não oferece o par que o componente já tem.
      qc.invalidateQueries({ queryKey: ["componente-candidatos", r.componenteId] })
    },
  })
}

/**
 * **IRREVERSÍVEL.** Quebra o componente em vários, um por componente interno
 * achado nos pinouts das ligações. Insere e realoca; não tem desfazer.
 */
export function usarTransformarInterno(projetoId: number, diagramaId: number) {
  const derrubar = usarDerrubar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (componenteId: number) => {
      const r = await api.post<{ componentesCriados: number; ligacoesRealocadas: number }>(
        `api/diagramas/componentes/${componenteId}/transformar-componente-interno`,
        undefined, noProjeto(projetoId))
      return r.data
    },
    onSuccess: derrubar,
  })
}
